import { WrapperWidget } from '@agentselly/ui';
import { useAuthz } from '~/context/AuthzContext';
import {
  Calendar,
  User2,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
} from 'lucide-react';
import { formatTime } from '~/helper/formatTime';
import { useTranslate } from '@tolgee/react';
import { formatDate } from '~/helper/formatDate';
import { formatWeekday } from '~/helper/formatWeedDate';

interface Props {
  readonly title: string;
  readonly data: any[];
  readonly infoText?: string;
}

export function CockpitCalendarEventDetailWidget(props: Props) {
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const showDetails = atLeast('partner');

  const groupedByDate: { [key: string]: any[] } = {};
  // Sort: group by date (yyyy-mm-dd), each date sorted by time ascending
  const items = [...(props.data || [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  items.forEach((event) => {
    const dateStr = new Date(event.date).toISOString().split('T')[0];
    if (!groupedByDate[dateStr]) {
      groupedByDate[dateStr] = [];
    }
    groupedByDate[dateStr].push(event);
  });

  const hasEvents = Object.keys(groupedByDate).length > 0;

  return (
    <WrapperWidget>
      <div className="h-full flex flex-col md:flex-row">
        <div className="flex flex-col justify-between w-full pr-4 mb-4 md:mb-0">
          <div className="flex flex-row gap-x-2 gap-y-2">
            <h2 className="text-lg font-bold flex-shrink-0 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary-600" />
              {t(props.title)}
            </h2>
          </div>

          {hasEvents ? (
            Object.entries(groupedByDate).map(
              (
                [
                  date,
                  events,
                ],
                idx,
              ) => (
                <div key={idx}>
                  <h3 className="text-lg md:text-xl font-bold mt-8 mb-2">
                    {formatWeekday(new Date(date))},{' '}
                    {formatDate(new Date(date))}
                  </h3>
                  {/* Day host summary */}
                  <div className="mt-4 mb-1 flex items-center gap-3">
                    <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Zuständig
                    </span>
                    <div className="flex -space-x-2">
                      {Array.from(
                        new Map(
                          events.map((e: any) => [
                            `${e?.hostDetails?.email || ''}-${e?.hostedBy}`,
                            e,
                          ]),
                        ).values(),
                      )
                        .slice(0, 3)
                        .map((e: any, i: number) =>
                          e?.hostDetails?.photoUrl ? (
                            <img
                              src={e.hostDetails.photoUrl}
                              alt="Host"
                              className="h-7 w-7 rounded-full ring-2 ring-white object-cover"
                            />
                          ) : (
                            <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-400 ring-2 ring-white">
                              {e?.hostDetails?.firstname?.[0] ||
                                (e?.hostedBy === 'internal' ? 'I' : 'P')}
                              {e?.hostDetails?.lastname?.[0] || ''}
                            </div>
                          ),
                        )}
                      {Array.from(
                        new Map(
                          events.map((e: any) => [
                            `${e?.hostDetails?.email || ''}-${e?.hostedBy}`,
                            e,
                          ]),
                        ).values(),
                      ).length > 3 && (
                        <div className="h-7 w-7 rounded-full bg-gray-100 dark:bg-gray-800 text-[10px] ring-2 ring-white flex items-center justify-center text-gray-600 dark:text-gray-400">
                          +
                          {Array.from(
                            new Map(
                              events.map((e: any) => [
                                `${e?.hostDetails?.email || ''}-${e?.hostedBy}`,
                                e,
                              ]),
                            ).values(),
                          ).length - 3}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timeline (group by time) */}
                  {(() => {
                    const groupsMap = new Map<string, any[]>();
                    for (const ev of events) {
                      const key = formatTime(ev.date);
                      const arr = groupsMap.get(key) || [];
                      arr.push(ev);
                      groupsMap.set(key, arr);
                    }
                    const timeGroups = Array.from(groupsMap.entries());
                    return (
                      <ol className="relative mt-2 border-s border-gray-200 dark:border-gray-700">
                        {timeGroups.map(
                          (
                            [
                              time,
                              list,
                            ],
                            gi,
                          ) => {
                            const primaryHost =
                              list[0]?.hostedBy === 'internal'
                                ? 'internal'
                                : 'partner';
                            const bookedCount = list.filter(
                              (e: any) => e.booked,
                            ).length;
                            const freeCount =
                              (list as any[]).length - bookedCount;
                            return (
                              <li
                                key={gi}
                                className="ms-6 py-4"
                              >
                                <span
                                  className={`absolute -start-1.5 mt-1 h-3 w-3 rounded-full ring-2 ring-white ${
                                    primaryHost === 'internal'
                                      ? 'bg-primary-500'
                                      : 'bg-amber-500'
                                  }`}
                                />
                                <div className="flex flex-col gap-2">
                                  {/* Time row with count/status summary */}
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                      <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                                        {time}
                                      </div>
                                      {(list as any[]).length > 1 && (
                                        <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-200 dark:text-gray-300 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
                                          {(list as any[]).length}×
                                        </span>
                                      )}
                                      <div className="flex -space-x-2">
                                        {Array.from(
                                          new Map(
                                            (list as any[]).map((e: any) => [
                                              `${e?.hostDetails?.email || ''}-${
                                                e?.hostedBy
                                              }`,
                                              e,
                                            ]),
                                          ).values(),
                                        )
                                          .slice(0, 2)
                                          .map((e: any, i: number) =>
                                            e?.hostDetails?.photoUrl ? (
                                              <img
                                                src={e.hostDetails.photoUrl}
                                                alt="Host"
                                                className="h-6 w-6 rounded-full ring-2 ring-white object-cover"
                                              />
                                            ) : (
                                              <div className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-400 ring-2 ring-white">
                                                {e?.hostDetails
                                                  ?.firstname?.[0] ||
                                                  (e?.hostedBy === 'internal'
                                                    ? 'I'
                                                    : 'P')}
                                                {e?.hostDetails
                                                  ?.lastname?.[0] || ''}
                                              </div>
                                            ),
                                          )}
                                        {Array.from(
                                          new Map(
                                            (list as any[]).map((e: any) => [
                                              `${e?.hostDetails?.email || ''}-${
                                                e?.hostedBy
                                              }`,
                                              e,
                                            ]),
                                          ).values(),
                                        ).length > 2 && (
                                          <div className="h-6 w-6 rounded-full bg-gray-100 dark:bg-gray-800 text-[10px] ring-2 ring-white flex items-center justify-center text-gray-600 dark:text-gray-400">
                                            +
                                            {Array.from(
                                              new Map(
                                                (list as any[]).map(
                                                  (e: any) => [
                                                    `${
                                                      e?.hostDetails?.email ||
                                                      ''
                                                    }-${e?.hostedBy}`,
                                                    e,
                                                  ],
                                                ),
                                              ).values(),
                                            ).length - 2}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {bookedCount > 0 && (
                                        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                          <CheckCircle2 className="h-3.5 w-3.5" />
                                          {bookedCount}
                                        </span>
                                      )}
                                      {freeCount > 0 && (
                                        <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-200 dark:text-gray-300 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
                                          <XCircle className="h-3.5 w-3.5" />
                                          {freeCount}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Items in this time slot */}
                                  <div className="mt-1 space-y-3">
                                    {list.map((event: any, idx: number) => (
                                      <div
                                        key={idx}
                                        className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-md border border-gray-100 dark:border-gray-700 dark:border-gray-800 bg-white dark:bg-gray-800 dark:bg-gray-900 px-3 py-2 border-l-2 ${
                                          event.hostedBy === 'internal'
                                            ? 'border-l-primary-400'
                                            : 'border-l-amber-400'
                                        }`}
                                      >
                                        <div className="flex items-center gap-3 min-w-0">
                                          <div className="flex items-center gap-2 min-w-0">
                                            {event?.hostDetails?.photoUrl ? (
                                              <img
                                                src={event.hostDetails.photoUrl}
                                                alt="Host"
                                                className="h-8 w-8 rounded-full ring-2 ring-gray-100 dark:ring-gray-700 object-cover"
                                              />
                                            ) : (
                                              <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                                                {event.hostDetails
                                                  ?.firstname?.[0] ||
                                                  (event.hostedBy === 'internal'
                                                    ? 'I'
                                                    : 'P')}
                                                {event.hostDetails
                                                  ?.lastname?.[0] || ''}
                                              </div>
                                            )}
                                            <div className="flex items-center gap-2 min-w-0">
                                              <span className="truncate text-sm text-gray-900 dark:text-gray-100 max-w-[12rem]">
                                                {[
                                                  event.hostDetails?.firstname,
                                                  event.hostDetails?.lastname,
                                                ]
                                                  .filter(Boolean)
                                                  .join(' ') ||
                                                  (event.hostedBy === 'internal'
                                                    ? 'Interner Agent'
                                                    : 'Partner Agent')}
                                              </span>
                                              <span
                                                className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] ring-1 ring-inset ${
                                                  event.hostedBy === 'internal'
                                                    ? 'bg-primary-50 text-primary-700 ring-primary-200'
                                                    : 'bg-amber-50 text-amber-700 ring-amber-200'
                                                }`}
                                              >
                                                {event.hostedBy === 'internal'
                                                  ? 'Internal'
                                                  : 'Partner'}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Status per item (small) */}
                                          <div>
                                            {event.booked ? (
                                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                                <CheckCircle2 className="h-3 w-3" />
                                                {t(
                                                  'cockpit_calendar_events_availability_booked',
                                                )}
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-200 dark:text-gray-300 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
                                                <XCircle className="h-3 w-3" />
                                                {t(
                                                  'cockpit_calendar_events_availability_not_booked',
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        {event.booked && (
                                          <div className="flex items-start md:items-center gap-4 md:gap-6 min-w-0">
                                            <div className="flex flex-col text-sm min-w-0">
                                              <div className="inline-flex items-center gap-2 truncate text-gray-900 dark:text-gray-100">
                                                <User2 className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
                                                <span className="truncate max-w-[14rem]">
                                                  {event.bookedBy ||
                                                    'Keine Kontaktdaten'}
                                                </span>
                                              </div>
                                              {showDetails &&
                                                (event.bookedByEmail ||
                                                  event.bookedByPhone) && (
                                                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                                                    {event.bookedByEmail && (
                                                      <a
                                                        href={`mailto:${event.bookedByEmail}`}
                                                        className="inline-flex items-center gap-1 hover:text-primary-600"
                                                        data-track="immodossier cockpit calendar event mailto"
                                                      >
                                                        <Mail className="h-3 w-3" />
                                                        <span className="truncate max-w-[14rem]">
                                                          {event.bookedByEmail}
                                                        </span>
                                                      </a>
                                                    )}
                                                    {event.bookedByPhone && (
                                                      <a
                                                        href={`tel:${event.bookedByPhone}`}
                                                        className="inline-flex items-center gap-1 hover:text-primary-600"
                                                        data-track="immodossier cockpit calendar event tel"
                                                      >
                                                        <Phone className="h-3 w-3" />
                                                        <span>
                                                          {event.bookedByPhone}
                                                        </span>
                                                      </a>
                                                    )}
                                                  </div>
                                                )}
                                            </div>

                                            <span className="hidden md:inline-block h-5 w-px bg-gray-200 dark:bg-gray-700" />

                                            <div className="text-xs text-gray-600 dark:text-gray-400 min-w-0">
                                              {event.bookedBy &&
                                              event.interestedStatus
                                                ? t(
                                                    'cockpit_calendar_events_interested_status_' +
                                                      event.interestedStatus,
                                                  )
                                                : t(
                                                    'cockpit_calendar_events_interested_status_no',
                                                  )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </li>
                            );
                          },
                        )}
                      </ol>
                    );
                  })()}
                </div>
              ),
            )
          ) : (
            <p>{t('cockpit_calendar_events_no_events')}</p>
          )}
        </div>
      </div>
    </WrapperWidget>
  );
}
